import React, { useState, useCallback, useEffect } from 'react';

import { formatCurrency, formatCurrencyNoDecimal } from '../../utilities/numericFormat.js';

import SharedDataContext from './sharedDataContext.jsx';

//----------------------------------------------------------------

//* TODO: isDev and retrieveQuoteJourney PRE-DEPLOY
// TODO: MAKE 100% SURE THAT THIS IS SET TO FALSE (OR REMOVED COMPLETELY)
// const isDev = false;

const SharedDataState = (props) => {
  const initialState = {
    accessToken: null, //* We only get the Access Token from a create quote but we need it for all other calls to eLife so, store it here.
    isCoverStateValid: true, //* On the Quote Summary page, we need to tell other components in the stack that if the Total Premium is valid
    updatingQuoteSummary: false, //* This tells the createQuote not to be called on the layout page when we are editing on the Quote Summary page
    isQuoteConverted: false, //* Set to true once we have converted the quote
    updateOktaUser: false,
    showErrorModal: false,
    isOutcomeDataLoaded: false, //* Have we run and complete the Outcome page
    hasOutcomeResponseRun: false,
    isRetrievePolicyExpired: false, //* for CC, we need to know if the Policy is expired on Outcome page.
    isPageLoading: false,
    pdsUrl: null,
    additionalConditionsChanged: false,
    retrievedCoverDetails: [],
    retrieveQuoteJourney: false, //! TODO: retrieveQuoteJourney IMPORTANT PRE-DEPLOY MAKE 100% SURE THAT THIS IS SET TO FALSE
    isDev: false, //! TODO: isDev IMPORTANT PRE-DEPLOY MAKE 100% SURE THAT THIS IS SET TO FALSE (OR REMOVED COMPLETELY) */
    errorMessage: null,
    showPromo: false, //* is there a promo running (Discount OR Gift Card )
    giftCardCampaign: false,
    campaignData: {
      campaignRunning: false,
      startDate: null,
      endDate: null,
      imageUrl: null,
      titleBanner: null,
      titleCard: null,
      subTitle: null,
      cta: null,
      ctaUrl: null,
      primaryColour: null,
      secondaryColour: null,
      showGradient: false,
      showInOnline: false,
      showInCC: false,
      additionalTnC: '',
      tncUrl: '',
    },
  };

  //* We get this access token from a call to createQuote or updateQuote
  //* Only used during dev/test when isDev is set to true.
  const devAccessToken =
    import.meta.env.MODE === 'localdev' ? import.meta.env.VITE_TEMP_ACCESS_CODE : null;

  const [state, setState] = useState(initialState);
  console.log('🚀 ~ SharedDataState ~ state:', state);

  // const handleEventChange = useCallback(
  //   ({ name, value }) => {
  //     setState((prevState) => ({ ...prevState, [name]: value }));
  //   },
  //   [setState], // ✅ depends only on setState, which is stable from useState/useReducer
  // );

  // const formateStartEndDat = ({ preDate }) => {
  //   const dateParts = preDate.split('/');
  //   const day = parseInt(dateParts[0]);
  //   const month = parseInt(dateParts[1]) - 1;
  //   const year = parseInt(dateParts[2]);
  //   let newFormateDate = new Date(Date.UTC(year, month, day, 0, 0, 0));
  //   return newFormateDate;
  // };

  const handleEventChange = useCallback(
    ({ name, value }) => {
      setState((prevState) => {
        // Check if the property we are updating belongs inside campaignData
        if (name in prevState.campaignData) {
          return {
            ...prevState,
            campaignData: {
              ...prevState.campaignData,
              [name]: value,
            },
          };
        }

        // Otherwise, update the top-level property
        return { ...prevState, [name]: value };
      });
    },
    [setState],
  );

  useEffect(() => {
    const workOutPromoDates = () => {
      // const today = new Date();
      let shouldShowPromo = false;
      let isGiftCardCopy = false;

      if (state?.campaignData?.startDate != null) {
        //? months are 0-indexed: 0 = January
        //? Date.UTC returns a timestamp in milliseconds since the epoch
        //? const startDate = state?.campaignData?.startDate;
        // TODO: REMOVE TEMP DATE
        // let tempStartDate =
        //   state?.campaignData?.startDate === '11/04/2026'
        //     ? '11/03/2026'
        //     : state?.campaignData?.startDate;
        // const startDate = formateStartEndDat({ preDate: tempStartDate });
        // // const startDate = formateStartEndDat({ preDate: state?.campaignData?.startDate });
        // const endDate = formateStartEndDat({ preDate: state?.campaignData?.endDate }); //Hide at exactly midnight on the 26th March 2026 - Won't be visible on the 27th
        // shouldShowPromo = today >= startDate && today <= endDate;

        shouldShowPromo = true;

        isGiftCardCopy = state?.campaignData?.isGiftCardCampaign;
      }
      setState((prevState) => ({
        ...prevState,
        ['campaignRunning']: shouldShowPromo ? true : false,
      }));
      // }

      setState((prevState) => ({
        ...prevState,
        ['showPromo']: shouldShowPromo,
      }));

      setState((prevState) => ({
        ...prevState,
        ['giftCardCampaign']: isGiftCardCopy,
      }));
    };

    workOutPromoDates();
  }, [state?.campaignData]);

  const handleAdditionalConditionsChange = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      additionalConditionsChanged: !prevState.additionalConditionsChanged,
    }));
  }, [setState]);

  const openErrorModal = useCallback(
    ({ errorMessage = null }) => {
      handleEventChange({
        name: 'showErrorModal',
        value: true,
      });

      if (errorMessage != null) {
        handleEventChange({
          name: 'errorMessage',
          value: errorMessage,
        });
      }
    },
    [handleEventChange], // ✅ dependency
  );

  const setPdsUrl = (url) => {
    if (url) {
      handleEventChange({
        name: 'pdsUrl',
        value: url,
      });
    }
  };

  //* Cover details for a CC retrieval */
  const createCoverDisplay = (cover, insuredAmount, monthlyPremium) => {
    if (!insuredAmount && insuredAmount?.length <= 0) return;

    const insuredAmountLocal = formatCurrencyNoDecimal(insuredAmount);
    const monthlyPremiumLocal = formatCurrency(monthlyPremium);

    return `${cover} / ${insuredAmountLocal} / ${monthlyPremiumLocal} monthly`;
  };
  const setRetrievedCoverDetails = async (data) => {
    if (!data?.insuredParticipants[0]?.covers) return;

    const covers = data?.insuredParticipants[0]?.covers;
    const coverData = [];

    const lifePremiums = covers?.life?.premiums ? true : false;

    const lifeMonthly = lifePremiums
      ? covers?.life?.premiums['monthly']
        ? covers?.life?.premiums['monthly']
        : covers?.life['monthly']
      : 0;

    const lifeCoverDisplay =
      lifePremiums && covers?.life?.sumInsured
        ? createCoverDisplay('Life', covers?.life?.sumInsured, lifeMonthly)
        : null;

    coverData.push(lifeCoverDisplay);

    const ciPremiums = covers?.criticalIllness?.premiums ? true : false;

    const ciMonthly = ciPremiums
      ? covers?.criticalIllness?.premiums['monthly']
        ? covers?.criticalIllness?.premiums['monthly']
        : covers?.criticalIllness['monthly']
      : null;

    const criticalIllnessCoverDisplay =
      ciPremiums && covers?.criticalIllness?.sumInsured
        ? createCoverDisplay('Critical Illness', covers?.criticalIllness?.sumInsured, ciMonthly)
        : null;
    coverData.push(criticalIllnessCoverDisplay);

    const uwPremiums = covers?.unableToWork?.premiums ? true : false;
    const uwMonthly = uwPremiums
      ? covers?.unableToWork?.premiums['monthly']
        ? covers?.unableToWork?.premiums['monthly']
        : covers?.unableToWork['monthly']
      : null;

    const unableToWorkCoverDisplay =
      uwPremiums && covers?.unableToWork?.sumInsured
        ? createCoverDisplay(' Unable to work', covers?.unableToWork?.sumInsured, uwMonthly)
        : null;
    coverData.push(unableToWorkCoverDisplay);

    if (coverData) {
      handleEventChange({
        name: 'retrievedCoverDetails',
        value: coverData,
      });
    }
  };

  return (
    <SharedDataContext.Provider
      value={{
        state,
        accessToken: state.isDev === false ? state.accessToken : devAccessToken,
        isCoverStateValid: state.isCoverStateValid,
        updatingQuoteSummary: state.updatingQuoteSummary,
        handleEventChange: handleEventChange,
        handleAdditionalConditionsChange: handleAdditionalConditionsChange,
        updateOktaUser: state.updateOktaUser,
        showErrorModal: state.showErrorModal,
        openErrorModal,
        isOutcomeDataLoaded: state.isOutcomeDataLoaded,
        isQuoteConverted: state.isQuoteConverted,
        errorMessage: state.errorMessage,
        retrieveQuoteJourney: state.retrieveQuoteJourney,
        isRetrievePolicyExpired: state.isRetrievePolicyExpired,
        isPageLoading: state.isPageLoading,
        isDevShared: state.isDev,
        isApplied: true,
        pdsUrl: state.pdsUrl,
        additionalConditionsChanged: state.additionalConditionsChanged,
        setPdsUrl,
        retrievedCoverDetails: state.retrievedCoverDetails,
        setRetrievedCoverDetails,
        showPromo: state.showPromo,
        giftCardCampaign: state.giftCardCampaign,
        campaignData: state.campaignData,
        additionalTnC: state.additionalTnC,
        tncUrl: state.tncUrl,
      }}
    >
      {props.children}
    </SharedDataContext.Provider>
  );
};

export default SharedDataState;
